import React, { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  PasswordField,
  UserFormFields,
  validateUserFields,
  type UserFormErrors,
  type UserFormValues
} from './UserFormFields'

interface EditUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (userData: UserFormData) => void
  user: User | null
  loading?: boolean
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  isActive: boolean
  lastLogin?: string
  oabNumber?: string
  oabState?: string
}

type UserFormData = UserFormValues

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  loading = false
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'ASSISTENTE',
    isActive: true,
    oabNumber: '',
    oabState: ''
  })
  const [changePassword, setChangePassword] = useState(false)
  const [errors, setErrors] = useState<UserFormErrors>({})

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: '',
        role: user.role,
        isActive: user.isActive,
        oabNumber: user.oabNumber || '',
        oabState: user.oabState || ''
      })
      setChangePassword(false)
      setErrors({})
    }
  }, [user, isOpen])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setChangePassword(false)
      setErrors({})
    }
  }, [isOpen])

  const validateForm = (): boolean => {
    const newErrors = validateUserFields(formData)

    if (changePassword && !formData.password?.trim()) {
      newErrors.password = 'Nova senha é obrigatória'
    } else if (changePassword && formData.password && formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      const submitData = { ...formData }
      if (!changePassword) {
        delete submitData.password
      }
      onSubmit(submitData)
      // Não resetar aqui - deixar o componente pai controlar quando fechar o modal
    }
  }

  return (
    <Dialog open={isOpen && !!user} onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent size="md" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
          <DialogDescription>Atualize os dados, a função e o acesso do usuário.</DialogDescription>
        </DialogHeader>

        <form id="edit-user-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <UserFormFields
            idPrefix="edit-user"
            values={formData}
            errors={errors}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            passwordSlot={
              <>
                <div className="flex items-center gap-2">
                  <input
                    id="edit-user-change-password"
                    type="checkbox"
                    checked={changePassword}
                    onChange={(e) => setChangePassword(e.target.checked)}
                    className="size-4 rounded-sm border-input accent-primary"
                  />
                  <Label htmlFor="edit-user-change-password" className="font-normal">Alterar senha</Label>
                </div>
                {changePassword && (
                  <PasswordField
                    id="edit-user-password"
                    label="Nova Senha *"
                    value={formData.password || ''}
                    placeholder="Digite a nova senha"
                    error={errors.password}
                    onChange={(password) => setFormData((prev) => ({ ...prev, password }))}
                  />
                )}
              </>
            }
          />
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="edit-user-form" disabled={loading}>
            {loading && <Loader2 className="size-4 animate-spin" />}
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
